// Dice SFX. The base "roll" is a real recorded clatter — one of N samples in
// public/audio/dice/, picked at random each roll. Crit fanfare / fumble thud
// stay synthesised on top via Web Audio.
// Intended to play only on Display view (the TV); other views import the
// no-op default and call it freely.

import { duckFor } from './audioDuck';

const DICE_SAMPLES = [
  '/audio/dice/DiceRoll.mp3',
  '/audio/dice/Dice_A1-0001.mp3',
  '/audio/dice/Dice_A1-0002.mp3',
  '/audio/dice/Dice_A1-0003.mp3',
  '/audio/dice/Dice_A1-0004.mp3',
  '/audio/dice/Dice_A1-0005.mp3',
  '/audio/dice/Dice_A1-0006.mp3',
  '/audio/dice/Dice_A1-0007.mp3',
  '/audio/dice/Dice_A1-0008.mp3',
  '/audio/dice/Dice_A1-0009.mp3',
  '/audio/dice/Dice_A1-0010.mp3',
  '/audio/dice/Dice_A1-0011.mp3',
  '/audio/dice/Dice_A1-0012.mp3',
  '/audio/dice/Dice_A1-0013.mp3',
  '/audio/dice/Dice_A1-0014.mp3',
  '/audio/dice/Dice_A1-0015.mp3',
  '/audio/dice/Dice_A1-0016.mp3',
  '/audio/dice/Dice_A1-0017.mp3',
  '/audio/dice/Dice_A1-0018.mp3',
];

// Primary path: HTMLAudioElement (works reliably everywhere after one user
// gesture). When Web Audio is available, also route the element through a
// GainNode > 1.0 for amplification — but only if that setup doesn't throw.
// HTMLAudioElement still plays as the safety net.
const DICE_GAIN = 2.5; // ~2.5x perceived louder; high enough to cut through music, low enough to avoid harsh clipping

function playRandomDiceSample() {
  if (typeof window === 'undefined') return;
  const url = DICE_SAMPLES[Math.floor(Math.random() * DICE_SAMPLES.length)];
  let a;
  try {
    a = new Audio(url);
    a.volume = 1.0;
  } catch { return; }

  // Try to amplify via Web Audio. If anything throws (createMediaElementSource
  // failures, no AudioContext, suspended/locked state), we still get audible
  // playback from the HTMLAudioElement itself.
  const c = ctx();
  if (c) {
    try {
      if (c.state === 'suspended') c.resume().catch(() => {});
      const src = c.createMediaElementSource(a);
      const g = c.createGain();
      g.gain.value = DICE_GAIN;
      src.connect(g);
      g.connect(c.destination);
    } catch { /* fall back to plain element */ }
  }
  a.play().catch(() => {}); // browsers may block until first gesture
}

let _ctx = null;
function ctx() {
  if (typeof window === 'undefined') return null;
  if (_ctx) return _ctx;
  const C = window.AudioContext || window.webkitAudioContext;
  if (!C) return null;
  _ctx = new C();
  return _ctx;
}

// Resume the AudioContext on the first user gesture — browsers autosuspend
// audio until that's done. Touch and click both work.
let _gestureBound = false;
function bindResumeGesture() {
  if (_gestureBound || typeof window === 'undefined') return;
  _gestureBound = true;
  const resume = () => {
    const c = ctx();
    if (c && c.state === 'suspended') c.resume().catch(() => {});
  };
  window.addEventListener('pointerdown', resume, { once: false, passive: true });
  window.addEventListener('keydown',     resume, { once: false, passive: true });
}
bindResumeGesture();

// Three ascending bell tones for a nat 20.
function playFanfare(c, when) {
  const notes = [523.25, 659.25, 783.99]; // C5 E5 G5
  notes.forEach((freq, i) => {
    const osc = c.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = freq;
    const g = c.createGain();
    g.gain.setValueAtTime(0, when + i * 0.08);
    g.gain.linearRampToValueAtTime(0.18, when + i * 0.08 + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, when + i * 0.08 + 0.45);
    osc.connect(g);
    g.connect(c.destination);
    osc.start(when + i * 0.08);
    osc.stop(when + i * 0.08 + 0.5);
  });
}

// Low triangle thud for nat 1.
function playFumble(c, when) {
  const osc = c.createOscillator();
  osc.type = 'triangle';
  osc.frequency.setValueAtTime(120, when);
  osc.frequency.exponentialRampToValueAtTime(50, when + 0.45);
  const g = c.createGain();
  g.gain.setValueAtTime(0, when);
  g.gain.linearRampToValueAtTime(0.32, when + 0.02);
  g.gain.exponentialRampToValueAtTime(0.0001, when + 0.55);
  osc.connect(g);
  g.connect(c.destination);
  osc.start(when);
  osc.stop(when + 0.6);
}

/**
 * Play the appropriate SFX for a roll record.
 * @param {Object} roll  — diceRoll record { dice, crit, ... }
 */
export function playRollSound(roll) {
  if (!roll) return;
  // Duck any active music so the clatter cuts through; fanfare/fumble extend
  // the duck a touch so the synth tones aren't drowned either.
  const duckMs = roll.crit === 'crit' || roll.crit === 'fumble' ? 2200 : 1400;
  duckFor(duckMs, 0.15);
  // Real recorded clatter — one random sample per roll.
  playRandomDiceSample();

  // Crit / fumble layer on top via the Web Audio synth so they still pop.
  const c = ctx();
  if (!c) return;
  if (c.state === 'suspended') c.resume().catch(() => {});
  const t0 = c.currentTime + 0.01;
  if (roll.crit === 'crit') {
    playFanfare(c, t0 + 0.9);
  } else if (roll.crit === 'fumble') {
    playFumble(c, t0 + 0.9);
  }
}
